import { Div, Icon, Label, Loader, useWindowSize, Select, Touchable } from "@grassrootly/rn-core";
import { GroupStatsProps } from "../GroupTabs.types";
import { ScrollView, Image, Platform, View } from "react-native";
import { useGroupStats } from './useGroupStats';
import { GroupTopTeam } from '../group-details/GroupInfoPlayers';
import { useMemo, useState, useRef } from "react";
import moment from "moment";
import { PlayerRating } from "../../../shared";
import { images } from "../../../../layout/assets";

export default function GroupStats(props: GroupStatsProps) {
  const { topTeam, monthlyMembers, month, navigateMonth, loadingInProgress, mode, setMode, selectedSeason, setSelectedSeason, seasonOptions, selectedSeasonIsActive } = useGroupStats(props);
  const { width } = useWindowSize();
  const [sortBy, setSortBy] = useState<"experience" | "rating" | "goals" | "assists" | "games" | "win_efficiency" | "goal_difference">("experience");
  const sortOptions = useMemo(
    () => [
      { value: "experience", label: "EXP" },
      { value: "rating", label: "Rating" },
      { value: "goals", label: "Goals" },
      { value: "assists", label: "Assists" },
      { value: "games", label: "Matches played" },
      { value: "win_efficiency", label: "Result efficiency" },
      { value: "goal_difference", label: "Goal difference" },
    ],
    []
  );

  const isCurrentOrFuturePeriod = useMemo(() => {
    if (mode === "monthly") {
      const selected = moment(month, "MMMM YYYY").startOf("month");
      const current = moment().startOf("month");
      return selected.isSameOrAfter(current);
    } else if (mode === "yearly") {
      const selected = moment(month, "YYYY").startOf("year");
      const current = moment().startOf("year");
      return selected.isSameOrAfter(current);
    } else if (mode === "seasonal") {
      // For seasonal mode, treat active seasons as current/future and inactive seasons as past
      return selectedSeasonIsActive;
    } else if (mode === "alltime") {
      // All time is always considered current/ongoing
      return true;
    }
    return false;
  }, [month, mode, selectedSeasonIsActive]);

  const teamHeadline = useMemo(() => {
    if (mode === "yearly") {
      return isCurrentOrFuturePeriod ? "Team of the year race" : "Team of the year";
    } else if (mode === "monthly") {
      return isCurrentOrFuturePeriod ? "Team of the month race" : `Team of the month - ${month}`;
    } else if (mode === "seasonal") {
      return isCurrentOrFuturePeriod ? "Team of the season race" : "Team of the season";
    } else if (mode === "alltime") {
      return "All time best team";
    }
    return "Team of the season";
  }, [mode, isCurrentOrFuturePeriod, month]);

  const noDataMessage = useMemo(() => {
    if (mode === "yearly") {
      return isCurrentOrFuturePeriod
        ? "Hey, there was no matches played in this year. Here you will be able to see your top team, just play one game at least ☺"
        : "Hey, there was no matches played in this year.";
    } else if (mode === "monthly") {
      return isCurrentOrFuturePeriod
        ? "Hey, there was no matches played in this month. Here you will be able to see your top team, just play one game at least ☺"
        : "Hey, there was no matches played in this month.";
    } else if (mode === "seasonal") {
      return isCurrentOrFuturePeriod
        ? "Hey, there was no matches played in this season. Here you will be able to see your top team, just play one game at least ☺"
        : "Hey, there were no matches played in this season.";
    } else if (mode === "alltime") {
      return "Hey, there are no matches played yet. Here you will be able to see your top players of all time, just play one game at least ☺";
    }
    return "Hey, there were no matches played in this season.";
  }, [mode, isCurrentOrFuturePeriod]);

  // Compute awards for monthly, yearly, and seasonal modes when selected period is in the past
  const awards = useMemo(() => {
    if (isCurrentOrFuturePeriod || !monthlyMembers || mode === "alltime") return null as any;
    const arr = Array.isArray(monthlyMembers) ? monthlyMembers : Object.values(monthlyMembers as any);
    if (!arr || !arr.length) return null as any;

    // Filter out players with 0 games first
    const playersWithGames = arr.filter((it: any) => Number(it?.games_played ?? 0) > 0);
    if (!playersWithGames.length) return null as any;

    // Build arrays of tied winners per category
    const toMaxAndWinnersOn = (source: any[], selector: (x: any) => number, filter?: (x: any) => boolean) => {
      const filtered = (filter ? source.filter(filter) : source).filter((x: any) => selector(x) !== undefined && !isNaN(Number(selector(x))));
      if (!filtered.length) return { max: -Infinity, winners: [] as any[] };
      const max = filtered.reduce((m: number, it: any) => Math.max(m, Number(selector(it) ?? -Infinity)), -Infinity);
      const winners = filtered.filter((it: any) => Number(selector(it)) === max);
      return { max, winners };
    };

    // 50% of MAX_GAMES eligibility for Player of the Month/Year (rating-based award)
    const maxGames = playersWithGames.reduce((m: number, it: any) => Math.max(m, Number(it?.games_played ?? 0)), 0);
    const minRequiredGames = Math.ceil(0.5 * maxGames);
    // seasonal mode is excluded above; apply filter directly
    const eligibleForRating = playersWithGames.filter((it: any) => Number(it?.games_played ?? 0) >= minRequiredGames);

    const rating = toMaxAndWinnersOn(eligibleForRating, (x) => x.avg_rating, (x) => typeof x.avg_rating === "number" && x.avg_rating > 0);
    const goals = toMaxAndWinnersOn(playersWithGames, (x) => x.goals ?? 0);
    const assists = toMaxAndWinnersOn(playersWithGames, (x) => x.assists ?? 0);
    const exp = toMaxAndWinnersOn(playersWithGames, (x) => x.points_earned ?? 0);

    // Apply tie-breakers to select a single winner for certain categories
    const pickByFewestGames = (list: any[]) =>
      list
        .slice()
        .sort((a, b) => {
          const ga = typeof a.games_played === "number" ? a.games_played : Number.POSITIVE_INFINITY;
          const gb = typeof b.games_played === "number" ? b.games_played : Number.POSITIVE_INFINITY;
          if (ga !== gb) return ga - gb; // fewer games first
          return 0;
        })[0];

    const pickByMostGames = (list: any[]) =>
      list
        .slice()
        .sort((a, b) => {
          const ga = typeof a.games_played === "number" ? a.games_played : 0;
          const gb = typeof b.games_played === "number" ? b.games_played : 0;
          if (ga !== gb) return gb - ga; // more games first
          return 0;
        })[0];

    const ratingWinners = rating.winners.length > 1 ? [pickByMostGames(rating.winners)].filter(Boolean) : rating.winners;
    const goalsWinners = goals.winners.length > 1 ? [pickByFewestGames(goals.winners)].filter(Boolean) : goals.winners;
    const assistsWinners = assists.winners.length > 1 ? [pickByFewestGames(assists.winners)].filter(Boolean) : assists.winners;

    return {
      rating: ratingWinners,
      goals: goalsWinners,
      assists: assistsWinners,
      exp: exp.winners,
      counts: {
        rating: rating.winners.length,
        goals: goals.winners.length,
        assists: assists.winners.length,
        exp: exp.winners.length,
      },
      originals: {
        rating: rating.winners,
        goals: goals.winners,
        assists: assists.winners,
        exp: exp.winners,
      },
    } as {
      rating: any[];
      goals: any[];
      assists: any[];
      exp: any[];
      counts: { rating: number; goals: number; assists: number; exp: number };
      originals: { rating: any[]; goals: any[]; assists: any[]; exp: any[] };
    };
  }, [monthlyMembers, mode, isCurrentOrFuturePeriod]);

  const scrollRef = useRef<ScrollView | null>(null);
  const [statsSectionY, setStatsSectionY] = useState<number>(0);

  const goToStatsWithFilter = (filter: "experience" | "rating" | "goals" | "assists" | "goal_difference") => {
    setSortBy(filter);
    if (scrollRef.current) {
      try {
        scrollRef.current.scrollTo({ y: Math.max(0, statsSectionY - 8), animated: true });
      } catch {}
    }
  };

  return (
    <ScrollView ref={scrollRef} showsVerticalScrollIndicator={false} style={{ marginTop: -16 }}
      contentContainerStyle={{
        paddingBottom: Platform.OS !== "web" ? (() => {
          const arr = monthlyMembers ? Object.values(monthlyMembers as any) : [];
          const len = Array.isArray(arr) ? arr.length : 0;
          return len ? len * 20 : 20;
        })() : 0,
      }}
    >
      <>

        {/* Mode toggle - pill chips with icons */}
        <Div classNames={["flexRow", "justifyContentCenter", "mt-{8}", "mb-{12}"]}>
          <Div classNames={["flexRow", "gap8", "bgSecondaryMain", "rounded", "ph8", "pv4", "alignItemsCenter"]}>
            <Touchable onPress={() => setMode("monthly")}>
              <Div
                classNames={[
                  "ph8",
                  "pv4",
                  "rounded",
                  mode === "monthly" ? "bgPrimaryWashed" : "",
                ]}
              >
                <Div classNames={["flexRow", "alignItemsCenter", "gap4"]}>
                  <Icon
                    name="calendar_today"
                    size={16}
                    classNames={[mode === "monthly" ? "colorPrimary" : "colorSecondaryContrast"]}
                  />
                  <Label
                    type={mode === "monthly" ? "l4Bold" : "l4Regular"}
                    classNames={[mode === "monthly" ? "colorPrimary" : "colorSecondaryContrast"]}
                  >
                    Monthly
                  </Label>
                </Div>
              </Div>
            </Touchable>

            <Touchable onPress={() => setMode("yearly")}>
              <Div
                classNames={[
                  "ph8",
                  "pv4",
                  "rounded",
                  mode === "yearly" ? "bgPrimaryWashed" : "",
                ]}
              >
                <Div classNames={["flexRow", "alignItemsCenter", "gap4"]}>
                  <Icon
                    name="calendar_month"
                    size={16}
                    classNames={[mode === "yearly" ? "colorPrimary" : "colorSecondaryContrast"]}
                  />
                  <Label
                    type={mode === "yearly" ? "l4Bold" : "l4Regular"}
                    classNames={[mode === "yearly" ? "colorPrimary" : "colorSecondaryContrast"]}
                  >
                    Yearly
                  </Label>
                </Div>
              </Div>
            </Touchable>

            {seasonOptions.length > 0 && (
              <Touchable onPress={() => setMode("seasonal")}>
                <Div
                  classNames={[
                    "ph8",
                    "pv4",
                    "rounded",
                    mode === "seasonal" ? "bgPrimaryWashed" : "",
                  ]}
                >
                  <Div classNames={["flexRow", "alignItemsCenter", "gap4"]}>
                    <Icon
                      name="calendar_month"
                      size={16}
                      classNames={[mode === "seasonal" ? "colorPrimary" : "colorSecondaryContrast"]}
                    />
                    <Label
                      type={mode === "seasonal" ? "l4Bold" : "l4Regular"}
                      classNames={[mode === "seasonal" ? "colorPrimary" : "colorSecondaryContrast"]}
                    >
                      Season
                    </Label>
                  </Div>
                </Div>
              </Touchable>
            )}

            <Touchable onPress={() => setMode("alltime")}>
              <Div
                classNames={[
                  "ph8",
                  "pv4",
                  "rounded",
                  mode === "alltime" ? "bgPrimaryWashed" : "",
                ]}
              >
                <Div classNames={["flexRow", "alignItemsCenter", "gap4"]}>
                  <Icon
                    name="all_inclusive"
                    size={16}
                    classNames={[mode === "alltime" ? "colorPrimary" : "colorSecondaryContrast"]}
                  />
                  <Label
                    type={mode === "alltime" ? "l4Bold" : "l4Regular"}
                    classNames={[mode === "alltime" ? "colorPrimary" : "colorSecondaryContrast"]}
                  >
                    All Time
                  </Label>
                </Div>
              </Div>
            </Touchable>
          </Div>
        </Div>

        {/* Month/Year navigator or Season selector (placed below mode filters) */}
        {mode === "seasonal" ? (
          <Div classNames={["flexRow", "w100", "justifyContentCenter", "mt-{4}", "mb-{20}", "alignItemsCenter"]}>
            <Select
              options={seasonOptions as any}
              selectedOption={selectedSeason}
              onSelect={(item: any) => setSelectedSeason(item.value)}
              placeholderText="Select season"
              forceSelectedOption
            />
          </Div>
        ) : mode === "alltime" ? (
          // Hide the "All time" header for all time mode
          null
        ) : (
          <Div classNames={["flexRow", "justifyContentCenter", "mt-{4}", "mb-{20}"]}>
            <Div classNames={["flexRow", "alignItemsCenter", "gap12", "bgSecondaryMain", "rounded", "ph12", "pv4"]}>
              {/* Previous period */}
              <Touchable onPress={() => navigateMonth(-1)}>
                <Icon name="arrow_back_ios" size={20} />
              </Touchable>

              {/* Current period */}
              <Div classNames={["flexRow", "alignItemsCenter", "ph-{12}"]}>
                <Label classNames={["textCenter"]} type="l2Bold">
                  {month}
                </Label>
              </Div>

              {/* Next period (hidden/disabled for current or future) */}
              {!isCurrentOrFuturePeriod && (
                <Touchable onPress={() => navigateMonth(1)}>
                  <Icon name="arrow_forward_ios" size={20} />
                </Touchable>
              )}
            </Div>
          </Div>
        )}
        {/* Headline below month/year control */}
        <Div classNames={["mt-{8}", "mb-{12}", (mode === "alltime" || mode === "yearly" || mode === "monthly" || mode === "seasonal") ? "alignItemsCenter" : ""]}>
          <Label type="l1Bold" classNames={[(mode === "alltime" || mode === "yearly" || mode === "monthly" || mode === "seasonal") ? "textCenter" : ""]}>{teamHeadline}</Label>
        </Div>
        {loadingInProgress && <Loader size={32}></Loader>}
        {!loadingInProgress && (
          <GroupTopTeam
            loadingInfoPlayers={loadingInProgress}
            infoPlayers={topTeam}
            noDataMessage={noDataMessage}
            navigation={props.navigation}
          />
        )}

        {/* Awards section - for monthly, yearly, and seasonal modes in the past */}
        {!isCurrentOrFuturePeriod && awards && (
          <>
            <Div classNames={["mt-{20}", "mb-{12}", "alignItemsCenter"]}>
              <Label type="l1Bold" classNames={["textCenter"]}>{mode === "seasonal" ? "Awards" : `Awards - ${month}`}</Label>
            </Div>

            {(() => {
              const isYearly = mode === "yearly";
              const isSeasonal = mode === "seasonal";
              const counts = (awards as any).counts as { rating: number; goals: number; assists: number; exp: number };
              const originals = (awards as any).originals as { rating: any[]; goals: any[]; assists: any[]; exp: any[] };
              
              const getAwardTitle = (baseTitle: string) => {
                if (isYearly) return `${baseTitle} of the year Award`;
                if (isSeasonal) return `${baseTitle} of the season Award`;
                return `${baseTitle} of the month Award`;
              };
              
              const items: Array<{ key: string; title: string; items: any[]; originalItems: any[]; kind: "rating" | "goals" | "assists" | "exp"; extraCount: number }> = [
                { key: "player", title: getAwardTitle("Player"), items: (awards as any).rating, originalItems: originals.rating, kind: "rating", extraCount: Math.max(0, (counts?.rating ?? 0) - 1) },
                { key: "goals", title: getAwardTitle("Goalscorer"), items: (awards as any).goals, originalItems: originals.goals, kind: "goals", extraCount: Math.max(0, (counts?.goals ?? 0) - 1) },
                { key: "assists", title: getAwardTitle("Assistant"), items: (awards as any).assists, originalItems: originals.assists, kind: "assists", extraCount: Math.max(0, (counts?.assists ?? 0) - 1) },
                { key: "exp", title: isSeasonal ? "Most EXP gained Award" : (isYearly ? "Most exp gained Award" : "Most EXP gained Award"), items: (awards as any).exp, originalItems: originals.exp, kind: "exp", extraCount: Math.max(0, (counts?.exp ?? 0) - 1) },
              ];

              const renderRight = (it: any, kind: "rating" | "goals" | "assists" | "exp") => {
                if (!it) return null;
                if (kind === "rating" && typeof it.avg_rating === "number" && it.avg_rating > 0) {
                  // Use the same PlayerRating widget style as the stats list below
                  return (
                    <Div classNames={["bgPrimaryWashed", "p8", "rounded"]}>
                      <PlayerRating rating={Number(Number(it.avg_rating).toFixed(2))} decimalPlaces={2} classNames={["p-{4}"]} />
                    </Div>
                  );
                }
                if (kind === "goals") {
                  return (
                    <Div classNames={["bgPrimaryWashed", "p8", "rounded", "flexRow", "alignItemsCenter", "gap4"]}>
                      <Label classNames={["colorSecondaryContrast"]} type={width > 720 ? "l2Regular" : "l3Regular"}>
                        {it.goals ?? 0}
                      </Label>
                      <Icon name="sports_soccer" size={18} />
                    </Div>
                  );
                }
                if (kind === "assists") {
                  return (
                    <Div classNames={["bgPrimaryWashed", "p8", "rounded", "flexRow", "alignItemsCenter", "gap4"]}>
                      <Label classNames={["colorSecondaryContrast"]} type={width > 720 ? "l2Regular" : "l3Regular"}>
                        {it.assists ?? 0}
                      </Label>
                      <Image source={images.assist} style={{ height: 14, width: 24 }} />
                    </Div>
                  );
                }
                if (kind === "exp") {
                  return (
                    <Div classNames={["bgPrimaryWashed", "p8", "rounded"]}>
                      <Label classNames={["colorSecondaryContrast"]} type={width > 720 ? "l2Bold" : "l3Bold"}>
                        {it.points_earned ?? 0}
                      </Label>
                    </Div>
                  );
                }
              };

              // group into rows of 2
              const rows: typeof items[] = [] as any;
              for (let i = 0; i < items.length; i += 2) {
                rows.push(items.slice(i, i + 2));
              }

              const avatarSize = width > 720 ? 88 : 68;

              return rows.map((rowItems, rowIdx) => (
                <Div key={`awards-row-${rowIdx}`} classNames={["flexRow", "gap16", "mb-{12}"]}>
                  {rowItems.map(({ key, title, items: winners, originalItems, kind, extraCount }, colIdx) => (
                    <Touchable
                      key={`${key}-${colIdx}`}
                      classNames={["flex1", `${width > 720 ? "p16" : "p12"}`, "bgSecondaryMain", "rounded", "flexColumn", "gap8"]}
                      onPress={undefined}
                      disabled={!winners || winners.length === 0}
                    >
                      {/* Title on top of card for emphasis */}
                      <Div classNames={["pl-{12}", "pt-{8}"]}>
                        <Div classNames={["flexRow", "alignItemsCenter", "gap8"]}>
                          {kind === "rating" && <Icon name="emoji_events" size={width > 720 ? 22 : 18} />}
                          {kind === "goals" && <Icon name="sports_soccer" size={width > 720 ? 22 : 18} />}
                          {kind === "assists" && (
                            <Image source={images.assist} style={{ height: width > 720 ? 16 : 14, width: width > 720 ? 28 : 24 }} />
                          )}
                          {kind === "exp" && <Icon name="analytics" size={width > 720 ? 22 : 18} />}
                          <Label type={width > 720 ? "l2Bold" : "l3Bold"}>{title}</Label>
                        </Div>
                      </Div>

                      {Array.isArray(winners) && winners.length > 0 && (
                        <Div classNames={["flexColumn", "gap8", "flex1", "pl-{8}", "pb-{12}"]}>
                          {winners.map((it: any, idx: number) => (
                            <Div key={`${key}-winner-${it.player_id ?? idx}`} classNames={["flexColumn", "gap8"]}>
                              {/* Top row: Avatar and metric */}
                              <Div classNames={["flexRow", "alignItemsCenter", "gap16"]}>
                                <Div classNames={["alignItemsCenter"]}>
                                  {Array.isArray(originalItems) && originalItems.length > 1 ? (() => {
                                    const base = Math.max(48, Math.min(72, avatarSize - 4));
                                    const second = originalItems.find((p: any) => p?.player_id !== it?.player_id);
                                    const hasSecond = !!second;
                                    return (
                                      <View style={{ width: hasSecond ? base + Math.floor(base * 0.4) : base, height: base, position: "relative" }}>
                                        {/* Primary winner on top */}
                                        <Image
                                          style={{
                                            position: "absolute",
                                            left: 0,
                                            top: 0,
                                            height: base,
                                            width: base,
                                            borderRadius: base,
                                            zIndex: 2,
                                          }}
                                          resizeMode="cover"
                                          source={it?.player_profile_picture ? { uri: it.player_profile_picture } : images.playerAvatar}
                                        />
                                        {/* Second candidate peeking 60% */}
                                        {hasSecond && (
                                          <Image
                                            style={{
                                              position: "absolute",
                                              left: Math.floor(base * 0.4),
                                              top: 0,
                                              height: base,
                                              width: base,
                                              borderRadius: base,
                                              zIndex: 1,
                                            }}
                                            resizeMode="cover"
                                            source={second?.player_profile_picture ? { uri: second.player_profile_picture } : images.playerAvatar}
                                          />
                                        )}
                                      </View>
                                    );
                                  })() : (
                                    <Div classNames={["bgPrimaryWashed", "p8", "rounded", "alignItemsCenter"]}>
                                      <Image
                                        style={{ height: Math.max(48, Math.min(72, avatarSize - 4)), width: Math.max(48, Math.min(72, avatarSize - 4)), borderRadius: Math.max(48, Math.min(72, avatarSize - 4)) }}
                                        resizeMode="cover"
                                        source={it?.player_profile_picture ? { uri: it.player_profile_picture } : images.playerAvatar}
                                      />
                                    </Div>
                                  )}
                                </Div>
                                <Div classNames={["flexRow", "alignItemsCenter", "gap8", "pr-{12}", "ml-{auto}"]}>{renderRight(it, kind)}</Div>
                              </Div>
                              
                              {/* Bottom section: Names centered full width */}
                              <Div classNames={["w100", "alignItemsCenter", "justifyContentCenter"]}>
                                <Label numberOfLines={2} ellipsizeMode="tail" type={width > 720 ? "l2Bold" : "l3Bold"} classNames={["textCenter"]}>
                                  {it?.player_name ?? "—"}
                                </Label>
                                {Array.isArray(originalItems) && originalItems.length > 1 && (
                                  <Div classNames={["flexColumn", "alignItemsCenter", "gap4", "justifyContentCenter", "w100", "mt-{4}"]}>
                                    {originalItems
                                      .filter((p: any) => p?.player_id !== it?.player_id)
                                      .map((p: any, idx2: number) => (
                                        <Label key={`extra-${p?.player_id ?? idx2}`} type={width > 720 ? "l2Bold" : "l3Bold"} classNames={["textCenter"]}>
                                          {p?.player_name}
                                        </Label>
                                      ))}
                                  </Div>
                                )}
                              </Div>
                            </Div>
                          ))}
                        </Div>
                      )}
                    </Touchable>
                  ))}
                  {rowItems.length === 1 && (
                    <Div classNames={["flex1"]} />
                  )}
                </Div>
              ));
            })()}
          </>
        )}

        {/* Stats headline below top team */}
        <View onLayout={(e: any) => setStatsSectionY(e?.nativeEvent?.layout?.y ?? 0)}>
          <Div classNames={["mt-{20}", "mb-{8}", (mode === "alltime" || mode === "yearly" || mode === "monthly" || mode === "seasonal") ? "alignItemsCenter" : ""]}>
            {mode === "seasonal" ? (
              <Label type="l1Bold" classNames={["textCenter"]}>{`Top stats - ${selectedSeason}`}</Label>
            ) : mode === "alltime" ? (
              <Label type="l1Bold" classNames={["textCenter"]}>All time top stats</Label>
            ) : mode === "yearly" ? (
              <Label type="l1Bold" classNames={["textCenter"]}>{`Top stats - ${month}`}</Label>
            ) : mode === "monthly" ? (
              <Label type="l1Bold" classNames={["textCenter"]}>{`Top stats - ${month}`}</Label>
            ) : (
              <Label type="l1Bold">{isCurrentOrFuturePeriod ? "Current stats" : `Top stats in - ${month}`}</Label>
            )}
          </Div>
        </View>

        {/* Monthly members table - same as Members tab */}
        {!loadingInProgress && monthlyMembers && (
          <>
            {/* Sorting control */}
            <Div classNames={["flexRow", "w100", "justifyContentFlexEnd", "mt-{12}", "mb-{8}", "alignItemsCenter", "gap8", "ml-{auto}"]}>
              <Select
                options={sortOptions as any}
                selectedOption={sortBy}
                onSelect={(item: any) => setSortBy(item.value)}
                placeholderText="Select filter"
                forceSelectedOption
              />
            </Div>

            {(() => {
              const arr = Array.isArray(monthlyMembers) ? monthlyMembers : Object.values(monthlyMembers as any);
              const filtered = arr.filter((item: any) => (item.games_played ?? 0) > 0);
              const sorted = filtered
                .slice()
                .sort((a: any, b: any) => {
                  switch (sortBy) {
                    case "experience":
                      return (b.points_earned ?? 0) - (a.points_earned ?? 0);
                    case "rating":
                      return ((b.avg_rating ?? 0) as number) - ((a.avg_rating ?? 0) as number);
                    case "goals":
                      return ((b.goals ?? 0) as number) - ((a.goals ?? 0) as number);
                    case "assists":
                      return ((b.assists ?? 0) as number) - ((a.assists ?? 0) as number);
                    case "games":
                      return ((b.games_played ?? 0) as number) - ((a.games_played ?? 0) as number);
                    case "win_efficiency":
                      return ((b.player_win_efficiency ?? 0) as number) - ((a.player_win_efficiency ?? 0) as number);
                    case "goal_difference":
                      return ((b.goal_difference ?? 0) as number) - ((a.goal_difference ?? 0) as number);
                    default:
                      return 0;
                  }
                });
              return sorted.map((item: any, index: number) => (
                <Touchable
                  key={`${item.player_id}-${index}`}
                  classNames={[
                    `${width > 720 ? "p16" : "p8"}`,
                    index % 2 !== 0 ? "bgSecondaryMain" : "",
                    "flexRow",
                    "alignItemsCenter",
                  ]}
                  onPress={() => props.navigation?.navigate("eqipa-card", { playerId: item.player_id })}
                >
                  <Div classNames={["flex1"]}>
                    <Div classNames={["flexRow", "alignItemsCenter", "justifyContentSpaceBetween"]}>
                      <Div classNames={["alignItemsCenter"]}>
                        <Div classNames={["bgPrimaryWashed", "p8", "rounded"]}>
                          <Image
                            style={{ height: width > 720 ? 40 : 30, width: width > 720 ? 40 : 30, borderRadius: 40 }}
                            resizeMode="cover"
                            source={item.player_profile_picture ? { uri: item.player_profile_picture } : images.playerAvatar}
                          />
                        </Div>
                      </Div>
                      <Div>
                        <Label numberOfLines={2} ellipsizeMode="tail" type={width > 720 ? "l2Regular" : "l3Regular"}>
                          {item.player_name}
                        </Label>
                      </Div>

                      <Div classNames={["flexRow", "alignItemsCenter", "gap8"]}>
                        {sortBy === "rating" && typeof (item as any).avg_rating === "number" && (item as any).avg_rating > 0 && (
                          <Div classNames={["bgPrimaryWashed", "p8", "rounded"]}>
                            <PlayerRating rating={Number((item as any).avg_rating.toFixed(2))} decimalPlaces={2} classNames={["p-{4}"]} />
                          </Div>
                        )}
                        {sortBy === "experience" && (
                          <Div classNames={["bgPrimaryWashed", "p8", "rounded", "flexRow", "alignItemsCenter", "gap4"]}>
                            <Label classNames={["colorSecondaryContrast"]} type={width > 720 ? "l2Regular" : "l3Regular"}>
                              {item.points_earned}
                            </Label>
                            <Label classNames={["colorSecondaryContrast"]} type={width > 720 ? "l2Regular" : "l3Regular"}>
                              exp.
                            </Label>
                          </Div>
                        )}
                        {sortBy === "goals" && (
                          <Div classNames={["bgPrimaryWashed", "p8", "rounded", "flexRow", "alignItemsCenter", "gap4"]}>
                            <Label classNames={["colorSecondaryContrast"]} type={width > 720 ? "l2Regular" : "l3Regular"}>
                              {(item as any).goals ?? 0}
                            </Label>
                            <Icon name="sports_soccer" size={18} />
                          </Div>
                        )}
                        {sortBy === "assists" && (
                          <Div classNames={["bgPrimaryWashed", "p8", "rounded", "flexRow", "alignItemsCenter", "gap4"]}>
                            <Label classNames={["colorSecondaryContrast"]} type={width > 720 ? "l2Regular" : "l3Regular"}>
                              {(item as any).assists ?? 0}
                            </Label>
                            <Image source={images.assist} style={{ height: 14, width: 24 }} />
                          </Div>
                        )}
                        {sortBy === "games" && (
                          <Div classNames={["bgPrimaryWashed", "p8", "rounded", "flexRow", "alignItemsCenter", "gap4"]}>
                            <Label classNames={["colorSecondaryContrast"]} type={width > 720 ? "l2Regular" : "l3Regular"}>
                              {(item as any).games_played ?? 0}
                            </Label>
                          </Div>
                        )}
                        {sortBy === "win_efficiency" && (
                          <Div classNames={["bgPrimaryWashed", "p8", "rounded", "flexRow", "alignItemsCenter", "gap4"]}>
                            <Label classNames={["colorSecondaryContrast"]} type={width > 720 ? "l2Regular" : "l3Regular"}>
                              {Number((((item as any).player_win_efficiency ?? 0) * 100).toFixed(1))}
                            </Label>
                            <Label classNames={["colorSecondaryContrast"]} type={width > 720 ? "l2Regular" : "l3Regular"}>
                              %
                            </Label>
                          </Div>
                        )}
                        {sortBy === "goal_difference" && (
                          <Div classNames={["bgPrimaryWashed", "p8", "rounded", "flexRow", "alignItemsCenter", "gap4"]}>
                            <Label classNames={["colorSecondaryContrast"]} type={width > 720 ? "l2Regular" : "l3Regular"}>
                              {(item as any).goal_difference ?? 0}
                            </Label>
                          </Div>
                        )}
                      </Div>
                    </Div>
                  </Div>
                </Touchable>
              ));
            })()}
          </>
        )}
      </>
    </ScrollView>
  );
}
